/*
 * 照片假解冻(冷归档取回)接口。
 *
 * 免费团队 1 年前的照片会被转成冷归档，付费后需要「解冻」才能查看，不是付费即时可见。
 * 服务端按团队放量(标签 freezePhotoGroup)，前端不自己判定团队是否命中，只认接口返回的 isHit。
 *
 * 接口文档：WebXCameraAPI 仓库 develop 分支 docs/0828.md
 */
import fetchHelper from '../../../utils/fetchHelper'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { store } from '../../../app/store'

declare const __API_SERVER_HOST__: string

const THAW_API = {
  summary: '/v5/workgroup/photos/temporary/thaw/task/summary',
  detail: '/v5/workgroup/photos/temporary/thaw/task/detail',
  activate: '/v5/workgroup/photos/temporary/thaw/task/activate'
} as const

/** 团队当前的解冻任务概况，团队级别，不随照片筛选条件变化 */
export interface TemporaryThawTask {
  /**
   * 是否存在解冻任务。
   * 服务端该字段所在的 task 是值类型而非指针，JSON 里恒为对象，判断有无任务要看这个字段，
   * 不能判 task 是否为 null。
   */
  exist: boolean
  /** 本次任务需解冻的照片总数 */
  totalPhotoCount: number
  /** 已完成解冻的照片数 */
  completedPhotoCount: number
  /** 任务是否已激活。false 表示需要用户手动触发取回 */
  isActivated: boolean
  /** 当前可见日期，格式 YYYY-MM-DD，早于该日期的照片处于冷冻状态 */
  visibleDate: string
}

export interface TemporaryThawSummary {
  /** 团队是否命中假解冻实验，未命中时其余字段无意义 */
  isHit: boolean
  /** 当前待解冻照片数量，团队总数，不受照片筛选条件影响。为 0 表示没有待解冻照片 */
  pendingPhotoCount: number
  task: TemporaryThawTask
}

/** 解冻总任务，进度弹窗的「全部照片」卡片用 */
export interface TemporaryThawTotalTask {
  id: number
  /** 需要解冻的照片总数 */
  photoCount: number
  /** 月份子任务总数 */
  totalMonthCount: number
  /** 任务开始时间，Unix 秒 */
  startedAt: number
  /** 总进度，0-100 */
  progress: number
  completedPhotoCount: number
  /** 预计完成时间，Unix 秒 */
  expectedFinishedAt: number
}

/** 月份子任务，进度弹窗的按月列表用 */
export interface TemporaryThawMonthTask {
  id: number
  totalTaskID: number
  /** 照片月份，格式 YYYY-MM */
  photoMonth: string
  photoCount: number
  /** 执行顺序，从 1 开始 */
  sortNo: number
  /** 子任务进度，0-100 */
  progress: number
  completedPhotoCount: number
}

export interface TemporaryThawDetail {
  /** 是否存在最新任务 */
  exist: boolean
  /** 最新总任务，不存在任务时为 null */
  totalTask: TemporaryThawTotalTask | null
  /** 月份子任务列表，没有子任务时为空数组 */
  monthTasks: TemporaryThawMonthTask[]
}

interface ThawResponse<T> {
  code?: number
  data?: ({ status?: number } & T) | null
}

/** 请求体只有 groupID，三个解冻接口共用 */
function buildThawRequest(path: string, groupID: string) {
  return {
    url: __API_SERVER_HOST__ + path,
    arguments: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ groupID })
    }
  }
}

/** 只有 code=200 且 status=0 才算业务成功，其余按不可用处理 */
function isThawResponseOk<T>(result: ThawResponse<T>): boolean {
  return result?.code === 200 && result?.data?.status === 0
}

function normalizeThawTask(
  task: Partial<TemporaryThawTask> | null | undefined
): TemporaryThawTask {
  return {
    exist: !!task?.exist,
    totalPhotoCount: task?.totalPhotoCount ?? 0,
    completedPhotoCount: task?.completedPhotoCount ?? 0,
    isActivated: !!task?.isActivated,
    visibleDate: task?.visibleDate ?? ''
  }
}

/**
 * 获取团队的假解冻任务摘要，驱动照片蒙层与悬浮横条。
 *
 * 失败时返回 null 而不是抛错：解冻是附加功能，接口不可用时按「未命中实验」处理，
 * 让照片列表照常渲染，不阻断主流程。
 */
export async function fetchTemporaryThawSummary(
  groupID: string
): Promise<TemporaryThawSummary | null> {
  const config = buildThawRequest(THAW_API.summary, groupID)

  try {
    const result: ThawResponse<{
      isHit?: boolean
      pendingPhotoCount?: number
      task?: Partial<TemporaryThawTask> | null
    }> = await fetchHelper(store.dispatch, store.getState, config)
    if (!isThawResponseOk(result)) return null

    const data = result.data
    return {
      isHit: !!data?.isHit,
      pendingPhotoCount: data?.pendingPhotoCount ?? 0,
      task: normalizeThawTask(data?.task)
    }
  } catch (error) {
    capturePromiseException(error as Error)
    return null
  }
}

/**
 * 获取解冻任务详情，驱动进度弹窗的总进度与按月列表。
 *
 * 失败返回 null，调用方保留上一次的进度数据，不要把弹窗清空。
 */
export async function fetchTemporaryThawDetail(
  groupID: string
): Promise<TemporaryThawDetail | null> {
  const config = buildThawRequest(THAW_API.detail, groupID)

  try {
    const result: ThawResponse<{
      exist?: boolean
      totalTask?: Partial<TemporaryThawTotalTask> | null
      monthTasks?: Partial<TemporaryThawMonthTask>[] | null
    }> = await fetchHelper(store.dispatch, store.getState, config)
    if (!isThawResponseOk(result)) return null

    const data = result.data
    const totalTask = data?.totalTask
    return {
      exist: !!data?.exist,
      totalTask: totalTask
        ? {
            id: totalTask.id ?? 0,
            photoCount: totalTask.photoCount ?? 0,
            totalMonthCount: totalTask.totalMonthCount ?? 0,
            startedAt: totalTask.startedAt ?? 0,
            progress: totalTask.progress ?? 0,
            completedPhotoCount: totalTask.completedPhotoCount ?? 0,
            expectedFinishedAt: totalTask.expectedFinishedAt ?? 0
          }
        : null,
      monthTasks: (data?.monthTasks ?? []).map((month) => ({
        id: month.id ?? 0,
        totalTaskID: month.totalTaskID ?? 0,
        photoMonth: month.photoMonth ?? '',
        photoCount: month.photoCount ?? 0,
        sortNo: month.sortNo ?? 0,
        progress: month.progress ?? 0,
        completedPhotoCount: month.completedPhotoCount ?? 0
      }))
    }
  } catch (error) {
    capturePromiseException(error as Error)
    return null
  }
}

/**
 * 激活团队当前待激活的解冻任务。
 *
 * 任务是团队级单例，多个成员同时点击由服务端保证只生成一个任务。
 *
 * @returns 是否激活成功；失败返回 false，调用方需提示用户重试
 */
export async function activateTemporaryThawTask(
  groupID: string
): Promise<boolean> {
  const config = buildThawRequest(THAW_API.activate, groupID)

  try {
    const result: ThawResponse<{ taskID?: number; activated?: boolean }> =
      await fetchHelper(store.dispatch, store.getState, config)
    return isThawResponseOk(result) && !!result.data?.activated
  } catch (error) {
    capturePromiseException(error as Error)
    return false
  }
}
